import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Avatar from '@atlaskit/avatar';
import ChevronRightLargeIcon from '@atlaskit/icon/glyph/chevron-right-large';
import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import Tooltip from '@atlaskit/tooltip';
import { IconButton } from '@atlaskit/button/new';
import MoreIcon from '@atlaskit/icon/glyph/more';
import LabelIcon from '@atlaskit/icon/glyph/label';
import { classnames } from './utils';
import { COMPONENT_HIERARCHY_TYPES, OCTO_GLOBAL_PERMISSIONS } from '../../utils/consts';
import './NodeContentRenderer.css';
import { GridWrapper } from './utils/styles';
import ProjectsAvatarGroup from '../projects-avatar-group';
import { ASSIGNEE_TYPES_OBJECT } from '../assets/assigneeTypes.assets';
import OctoPermissionsContext from '../../providers/OctoPermissionsContext';
import { hasPermission } from '../../utils/commons';
import IssuesCountLink from '../virtual-components/IssuesCountLink';

const AvatarNameView = styled.div`
    p {
        margin-left: 7px;
        margin-top: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: calc(100% - 48px);
    }
    > * {
        display: inline-block;
        vertical-align: middle;
    }
`;
const TableColumnTextItem = styled.div`
    display: flex;
    align-items: center;
`;

const RowBefore = styled.div`
    &:before {
        margin-left: ${(props) => (props.left ? props.left : '0')};
    }
`;

/**
 * Check if a node is a descendant of another node.
 *
 * @param {!Object} older - Potential ancestor of younger node
 * @param {!Object} younger - Potential descendant of older node
 *
 * @return {boolean}
 */
function isDescendant(older, younger) {
    return (
        !!older.children &&
        typeof older.children !== 'function' &&
        older.children.some((child) => child === younger || isDescendant(child, younger))
    );
}

function DropdownOptions({ deleteAction, node }) {
    return (
        <DropdownItemGroup>
            {!node.children && !node.hasChildren && <DropdownItem onClick={deleteAction}>Delete</DropdownItem>}
            {(node.children || node.hasChildren) && (
                <Tooltip content="You can't delete label that has components assigned">
                    <DropdownItem isDisabled onClick={deleteAction}>
                        Delete
                    </DropdownItem>
                </Tooltip>
            )}
        </DropdownItemGroup>
    );
}

const MoreIconTrigger = (params) => {
    const { triggerRef, ...props } = params;
    return <IconButton {...props} icon={MoreIcon} label="more" ref={triggerRef} />;
};

class NodeRendererDefault extends Component {
    render() {
        const {
            scaffoldBlockPxWidth,
            toggleChildrenVisibility,
            connectDragPreview,
            connectDragSource,
            isDragging,
            canDrop,
            canDrag,
            node: nodeItem,
            title,
            subtitle,
            draggedNode,
            path,
            treeIndex,
            isSearchMatch,
            isSearchFocus,
            buttons,
            className,
            style,
            didDrop,
            treeId,
            isOver, // Not needed, but preserved for other renderers
            parentNode, // Needed for dndManager
            rowDirection,
            lowerSiblingCounts,
            listIndex,
            onClickName,
            onClickDeleteLabel,
            onExpandNode,
            ...otherProps
        } = this.props;
        const node = { ...nodeItem, projects: nodeItem.projects };
        const nodeTitle = title || node.name;
        const nodeSubtitle = subtitle || node.subtitle;
        const rowDirectionClass = rowDirection === 'rtl' ? 'rst__rtl' : null;

        let handle;
        // if (canDrag) {
        //   if (typeof node.children === 'function' && node.expanded) {
        //     // Show a loading symbol on the handle when the children are expanded
        //     //  and yet still defined by a function (a callback to fetch the children)
        //     handle = (
        //         <div className="rst__loadingHandle">
        //             <div className="rst__loadingCircle">
        //                 {[...new Array(12)].map((_, index) => (
        //                     <div
        //                         // eslint-disable-next-line react/no-array-index-key
        //                         key={index}
        //                         className={classnames('rst__loadingCirclePoint', rowDirectionClass)}
        //                     />
        //                 ))}
        //             </div>
        //         </div>
        //     );
        // } else {
        //     // Show the handle used to initiate a drag-and-drop
        //     // handle = connectDragSource(<div className="rst__moveHandle" />, {
        //     //   dropEffect: 'copy',
        //     // });
        // }
        // // }

        const isDraggedDescendant = draggedNode && isDescendant(draggedNode, node);
        const isLandingPadActive = !didDrop && isDragging;

        let buttonStyle = { position: 'absolute', left: lowerSiblingCounts.length * scaffoldBlockPxWidth - 10 };
        if (rowDirection === 'rtl') {
            buttonStyle = { right: -0.5 * scaffoldBlockPxWidth };
        }

        const nodeContent = connectDragPreview(
            <div style={{ height: '100%' }}>
                <RowBefore
                    className={classnames(
                        'rst__row',
                        isLandingPadActive && 'rst__rowLandingPad',
                        isLandingPadActive && !canDrop && 'rst__rowCancelPad',
                        isSearchMatch && 'rst__rowSearchMatch',
                        isSearchFocus && 'rst__rowSearchFocus',
                        rowDirectionClass,
                        className,
                    )}
                    style={{
                        ...style,
                        opacity: isDraggedDescendant ? 0.5 : 1,
                    }}
                    left={isLandingPadActive ? `${lowerSiblingCounts.length * scaffoldBlockPxWidth}px` : null}
                >
                    {handle}
                    <div
                        className={classnames(
                            'rst__rowContents',
                            !canDrag && 'rst__rowContentsDragDisabled',
                            rowDirectionClass,
                        )}
                    >
                        <GridWrapper
                            style={{ width: '100%' }}
                            onClick={() => {
                                toggleChildrenVisibility({
                                    node,
                                    path,
                                    treeIndex,
                                });
                                if (node.hasChildren) {
                                    onExpandNode();
                                }
                            }}
                        >
                            <div className={classnames('rst__rowLabel', rowDirectionClass)}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {node.type === COMPONENT_HIERARCHY_TYPES.LABEL && <LabelIcon />}
                                </div>
                                <span
                                    className={classnames(
                                        'rst__rowTitle',
                                        node.subtitle && 'rst__rowTitleWithSubtitle',
                                    )}
                                >
                                    {node.type === COMPONENT_HIERARCHY_TYPES.LABEL && nodeTitle}
                                    {node.type === COMPONENT_HIERARCHY_TYPES.COMPONENT && (
                                        <a onClick={() => onClickName(node)}>{nodeTitle}</a>
                                    )}
                                </span>

                                {nodeSubtitle && (
                                    <span className="rst__rowSubtitle">
                                        {typeof nodeSubtitle === 'function'
                                            ? nodeSubtitle({
                                                  node,
                                                  path,
                                                  treeIndex,
                                              })
                                            : nodeSubtitle}
                                    </span>
                                )}
                            </div>
                            {node.type === COMPONENT_HIERARCHY_TYPES.COMPONENT && (
                                <>
                                    <div>
                                        <ProjectsAvatarGroup virtualComponent={node} />
                                    </div>
                                    <div>
                                        {node.virtualComponentLead &&
                                            node?.virtualComponentLead?.jiraUserAccountId !== 'unknown' && (
                                                <AvatarNameView>
                                                    <Avatar src={node.virtualComponentLead.jiraUserAvatarUrl} />
                                                    <p>{node.virtualComponentLead.jiraUserDisplayName}</p>
                                                </AvatarNameView>
                                            )}
                                    </div>
                                    {!node.virtualComponentLead ||
                                        (node?.virtualComponentLead?.jiraUserAccountId === 'unknown' && '')}
                                    <TableColumnTextItem>
                                        <p
                                            style={{
                                                WebkitLineClamp: 1,
                                                overflow: 'hidden',
                                                display: '-webkit-box',
                                                WebkitBoxOrient: 'vertical',
                                            }}
                                        >
                                            {ASSIGNEE_TYPES_OBJECT[node.assigneeType]}
                                        </p>
                                    </TableColumnTextItem>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IssuesCountLink virtualComponent={node} />
                                    </div>
                                </>
                            )}
                        </GridWrapper>
                        {node.type === COMPONENT_HIERARCHY_TYPES.LABEL && (
                            <OctoPermissionsContext.Consumer>
                                {(permissions) => {
                                    if (hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions)) {
                                        return (
                                            <DropdownMenu trigger={MoreIconTrigger} position="bottom right">
                                                <DropdownOptions
                                                    deleteAction={() => {
                                                        onClickDeleteLabel(node);
                                                    }}
                                                    node={node}
                                                />
                                            </DropdownMenu>
                                        );
                                    }
                                }}
                            </OctoPermissionsContext.Consumer>
                        )}
                    </div>
                </RowBefore>
            </div>,
        );

        return (
            <div style={{ height: '100%' }} {...otherProps}>
                {toggleChildrenVisibility &&
                    (node.children || node.hasChildren) &&
                    (node.children?.length > 0 || typeof node.children === 'function' || node.hasChildren) && (
                        <div>
                            <div
                                style={buttonStyle}
                                className={classnames(
                                    node.expanded ? 'rst__collapseButton' : 'rst__expandButton',
                                    rowDirectionClass,
                                )}
                                onClick={() => {
                                    toggleChildrenVisibility({
                                        node,
                                        path,
                                        treeIndex,
                                    });
                                    onExpandNode();
                                }}
                            >
                                {/* {node.expanded && node.children?.length > 0 && <HipchatChevronDownIcon />} */}
                                {node.expanded && <HipchatChevronDownIcon />}
                                {!node.expanded && <ChevronRightLargeIcon />}
                            </div>
                        </div>
                    )}

                <div className={classnames('rst__rowWrapper', rowDirectionClass)}>
                    {/* Set the row preview to be used during drag and drop */}
                    {canDrag ? connectDragSource(nodeContent, { dropEffect: 'copy' }) : nodeContent}
                </div>
            </div>
        );
    }
}

NodeRendererDefault.defaultProps = {
    isSearchMatch: false,
    isSearchFocus: false,
    canDrag: false,
    toggleChildrenVisibility: null,
    buttons: [],
    className: '',
    style: {},
    parentNode: null,
    draggedNode: null,
    canDrop: false,
    title: null,
    subtitle: null,
    rowDirection: 'ltr',
};

NodeRendererDefault.propTypes = {
    node: PropTypes.shape({}).isRequired,
    title: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    subtitle: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    path: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
    treeIndex: PropTypes.number.isRequired,
    treeId: PropTypes.string.isRequired,
    isSearchMatch: PropTypes.bool,
    isSearchFocus: PropTypes.bool,
    canDrag: PropTypes.bool,
    scaffoldBlockPxWidth: PropTypes.number.isRequired,
    toggleChildrenVisibility: PropTypes.func,
    buttons: PropTypes.arrayOf(PropTypes.node),
    className: PropTypes.string,
    style: PropTypes.shape({}),

    // Drag and drop API functions
    // Drag source
    connectDragPreview: PropTypes.func.isRequired,
    connectDragSource: PropTypes.func.isRequired,
    parentNode: PropTypes.shape({}), // Needed for dndManager
    isDragging: PropTypes.bool.isRequired,
    didDrop: PropTypes.bool.isRequired,
    draggedNode: PropTypes.shape({}),
    // Drop target
    isOver: PropTypes.bool.isRequired,
    canDrop: PropTypes.bool,

    // rtl support
    rowDirection: PropTypes.string,
};

export default NodeRendererDefault;
